import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { throttle } from '@thd-olt-functional/utils';
import { useScrollLock } from './useMAScrollLock';
import {
  DRAWER_HEIGHTS,
  DRAG_THRESHOLD_PX,
  MINIMIZED_DRAG_MAX,
  OPEN_DRAG_MAX,
  TAP_DURATION_MS
} from '../components/MagicApronAssistant/constants';

/**
 * Custom hook to handle the state and logic of a draggable drawer
 *
 * @param { DrawerCommon } props - The properties for the useDraggableDrawer hook.
 * @returns {{
 *   handlePointerDown: (event: TouchEvent) => void,
 *   handlePointerMove: (event: TouchEvent) => void,
 *   handlePointerUp: () => void,
 *   dynamicHeight: string,
 *   dynamicBottom: string | undefined,
 * }}
 */
export const useDraggableDrawer = (drawerState, setDrawerState, currentHeight) => {
  /**
   * @type {[DrawerStatus, React.Dispatch<React.SetStateAction<DrawerStatus>>]}
   */
  const startY = useRef(null);
  const endY = useRef(null);
  const isDragging = useRef(false);
  const tapTimeout = useRef(null);
  const { lockScroll, unlockScroll } = useScrollLock();
  const [dragDistance, setDragDistance] = useState(0);
  // iOS never shrinks the layout viewport for the keyboard, so window.innerHeight
  // (and any CSS percentage resolved against it) describes an area the user cannot
  // fully see once the keyboard is up. The visual viewport is the unobscured area,
  // and it is what the drawer must be sized and positioned to.
  const [viewportHeight, setViewportHeight] = useState(
    () => window.visualViewport?.height ?? window.innerHeight
  );
  const [viewportOffsetTop, setViewportOffsetTop] = useState(
    () => window.visualViewport?.offsetTop ?? 0
  );
  const [cachedHeight, setCachedHeight] = useState(null);

  useEffect(() => {
    const visualViewport = window.visualViewport;
    if (!visualViewport) return undefined;

    // height changes on resize (the keyboard appearing or dismissing); offsetTop
    // changes on scroll (WebKit sliding the unobscured area to reveal a focused
    // input). Both have to be tracked to keep the drawer over the visible area.
    const syncViewport = () => {
      setViewportHeight(visualViewport.height);
      setViewportOffsetTop(visualViewport.offsetTop);
    };
    visualViewport.addEventListener('resize', syncViewport);
    visualViewport.addEventListener('scroll', syncViewport);

    return () => {
      visualViewport.removeEventListener('resize', syncViewport);
      visualViewport.removeEventListener('scroll', syncViewport);
    };
  }, []);

  // DRAWER_HEIGHTS and the drag maths are percentages. Emitting them as `vh` would
  // resolve them against the layout viewport while the resting height below uses the
  // visual viewport, so the drawer jumps the moment a drag starts. Keeping both in
  // the same space means turning percentages into pixels of the visible area.
  const toViewportPx = (percentage) => `${(percentage / 100) * viewportHeight}px`;

  const throttledCalculateHeight = throttle(
    16, // So UI animation is ~60fps
    (currentDragDistance) => {
      const dragRatio = Math.min(Math.abs(currentDragDistance / viewportHeight), 1.0) * Math.sign(currentDragDistance);
      const heightPercentage = (1 - dragRatio) * 100;
      const clampedHeight = Math.max(DRAWER_HEIGHTS.CLOSED, Math.min(heightPercentage, DRAWER_HEIGHTS.OPEN));
      // Adjust the height based on the drawer state max/min values
      const finalHeight = drawerState === 'MINIMIZED' ? Math.min(MINIMIZED_DRAG_MAX, clampedHeight)
        : Math.max(OPEN_DRAG_MAX, clampedHeight);
      const result = toViewportPx(finalHeight);
      setCachedHeight(result);
      return result;
    });

  const dynamicHeight = useMemo(() => {
    if (drawerState === 'OPEN' && !dragDistance) {
      return `calc(${viewportHeight}px - 1em)`;
    }
    if (!dragDistance) {
      return drawerState === 'MINIMIZED' ? currentHeight : toViewportPx(currentHeight);
    }
    const newHeight = throttledCalculateHeight(dragDistance);
    return newHeight || cachedHeight || toViewportPx(currentHeight);
  }, [drawerState, dragDistance, currentHeight, cachedHeight, viewportHeight]);

  // `bottom: 0` anchors the drawer to the bottom of the layout viewport, which sits
  // behind the keyboard — so the composer lands underneath it and WebKit shifts the
  // whole page to reveal it. Anchoring to the bottom of the unobscured area instead
  // puts the composer above the keyboard, leaving nothing for WebKit to reveal.
  // Resolves to 0 whenever the keyboard is down, which is the existing behaviour.
  const dynamicBottom = useMemo(() => {
    if (drawerState !== 'OPEN') return undefined;
    return `${window.innerHeight - viewportOffsetTop - viewportHeight}px`;
  }, [drawerState, viewportOffsetTop, viewportHeight]);

  const handlePointerDown = useCallback((event) => {
    startY.current = event?.clientY || event.touches?.[0]?.clientY;
    endY.current = startY.current;
    lockScroll();

    tapTimeout.current = setTimeout(() => {
      if (startY.current !== null) {
        isDragging.current = true;
      }
    }, TAP_DURATION_MS);
  }, [lockScroll]);

  const handlePointerMove = useCallback((event) => {
    if (startY.current === null) return;

    endY.current = event?.clientY || event.touches?.[0]?.clientY;
    setDragDistance(endY.current);
  }, []);

  const handleTap = () => {
    setDrawerState(drawerState === 'OPEN' ? 'MINIMIZED' : 'OPEN');
    setDragDistance(null);
  };

  const handleDragEnd = useCallback(() => {
    if (startY.current !== null && endY.current !== null) {
      const deltaY = endY.current - startY.current;

      let newDrawerState = drawerState;
      if (drawerState === 'MINIMIZED') {
        if (deltaY >= (DRAG_THRESHOLD_PX / 2)) newDrawerState = 'CLOSED';
        if (deltaY <= -DRAG_THRESHOLD_PX) newDrawerState = 'OPEN';
      }
      if (drawerState === 'OPEN') {
        if (deltaY >= DRAG_THRESHOLD_PX) newDrawerState = 'MINIMIZED';
      }
      setDrawerState(newDrawerState);
      setDragDistance(null);
    }
  }, [drawerState]);

  const handlePointerUp = useCallback(() => {
    if (drawerState !== 'OPEN') {
      unlockScroll();
    }

    clearTimeout(tapTimeout.current);

    if (isDragging.current) {
      handleDragEnd();
    } else {
      handleTap();
    }

    startY.current = null;
    endY.current = null;
    isDragging.current = false;
  }, [unlockScroll, handleDragEnd, handleTap, drawerState]);

  useEffect(() => {
    if (drawerState === 'OPEN') {
      lockScroll();
    } else {
      unlockScroll();
    }
  }, [drawerState, lockScroll, unlockScroll]);

  // Needed to ensure scroll is unlocked if component is closed/unmounted
  useEffect(() => {
    return () => {
      unlockScroll();
    };
  }, [unlockScroll]);

  return ({
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    dynamicHeight,
    dynamicBottom,
  });
};
